<template>
  <div class="platform-container">
    <div
      v-if="adminViewing && !mobileNavOpen && !defaultOpenFromCore"
      class="admin-banner label-m-regular cr-flex spc-p-1"
    >
      <span class="admin-banner-text cr-mx-auto">
        <span>{{ adminBannerText }}</span>
        &nbsp; - &nbsp;
        <a
          class="admin-banner-link"
          href="/admin/external_users/back_to_admin"
        >
          Return to Core
        </a>
      </span>
    </div>
    <div class="platform-mobile-header-container">
      <div class="platform-mobile-header">
        <div
          class="nav-button-container"
          :class="{ 'active': mobileNavOpen }"
        >
          <BaseIcon
            v-if="!defaultOpenFromCore"
            class="nav-button"
            :color="mobileNavOpen ? 'var(--salt)' : 'var(--blueberry-1000)'"
            font-size="1rem"
            :icon="mobileNavOpen ? 'far fa-x' : 'far fa-bars'"
            @click="mobileNavOpen = !mobileNavOpen"
          />
        </div>
        <router-link
          class="logo-container"
          :to="{name: 'client-home'}"
        >
          <img
            alt="Crafty"
            class="nav-crafty-logo"
            :src="craftyLogo"
          >
        </router-link>
        <div class="header-right-spacer" />
      </div>
    </div>
    <div
      class="platform-sidebar"
      :class="{ 'cr-none': !showSideNav }"
    >
      <div class="header-container">
        <router-link
          class="logo-container"
          :to="{name: 'client-home'}"
        >
          <img
            alt="Crafty"
            class="nav-crafty-logo"
            :src="craftyLogo"
          >
        </router-link>
        <div class="header-right-spacer" />
      </div>
      <ClientSideNav
        :logged-out-user="loggedOutUser"
      />
    </div>
    <div
      class="platform-body"
      :class="{ 'cr-none': $screenSize >= 768 || showSideNav }"
    >
      <div
        class="platform-content"
        :class="{ 'platform-content--homepage': $route.name === 'client-home', 'admin-viewing': adminViewing }"
      >
        <router-view />
        <CraftyCopilot v-if="showCopilot" />
      </div>
    </div>
    <DemoWalkthroughBanner v-if="isDemoUser" />
  </div>
</template>

<script>
import DemoWalkthroughBanner from '../components/DemoWalkthroughBanner.vue'
import ClientSideNav from '../components/ClientSideNav.vue'
import BaseIcon from '../components/BaseIcon.vue'
import CraftyCopilot from '../components/CraftyCopilot.vue'

export default {
  name: "ClientPlatformApp",
  components: {
    CraftyCopilot,
    DemoWalkthroughBanner,
    ClientSideNav,
    BaseIcon,
  },
  inject: [
    'isDemoUser',
    'loggedOutUser',
    'clientPlatformCopilotEnabled',
    'adminViewing',
    'externalUserFullName',
  ],
  props: {
    externalUserId: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      mobileNavOpen: false,
    }
  },
  computed: {
    craftyLogo() {
      return new URL('assets/images/logos/crafty_logo.png', import.meta.url).href
    },
    showCopilot() {
      return this.clientPlatformCopilotEnabled && !this.isOnAiHomepage
    },
    isOnAiHomepage() {
      return this.$route.name === 'client-home'
    },
    loggedOutTooltipText() {
      return 'You must be logged in to view this feature.'
    },
    showSideNav() {
      return this.$screenSize >= 769 || this.mobileNavOpen || this.defaultOpenFromCore
    },
    adminBannerText() {
      if (this.$screenSize.value < 769) {
        return `${this.externalUserFullName}`
      } else {
        return `Viewing ${this.externalUserFullName}'s Platform`
      }
    },
    defaultOpenFromCore() {
      return this.$route.path === '/client/dashboard'
    },
  },
  watch: {
    $route(newValue) {
      if (newValue) {
        this.mobileNavOpen = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.platform-body-wrapper {
  height: 100vh;
}
.platform-container {
  height: 100vh;
  display: flex;
  flex-direction: column;
}
.platform-sidebar {
  width: 100%;
  height: 100%;
  border-bottom-right-radius: 0;
  padding: 0;
  background: var(--salt);
  top: 0;
  left: 0;
  z-index: 0;

  .header-container {
    display: none;
  }
}
.platform-mobile-header-container {
  display: block;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  min-width: 100vw;
  z-index: 100;
  background-color: var(--salt);
  border-bottom: 1px solid var(--pepper-200);
}
.platform-mobile-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-4) var(--space-3);
}
.nav-button-container {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: 50%;

  &.active {
    background-color: var(--blueberry-1000);
  }
}
.nav-crafty-logo {
  height: 33px;
}
.platform-body {
  width: 100%;
  padding-left: 0px;
}
.platform-content {
  --client-y-margin: var(--space-3);

  flex: 1;
  overflow-y: inherit;
  margin: var(--client-y-margin);
  padding: 0;
  margin-top: 95px;

  &.platform-content--homepage {
    margin: 0;
    padding: 0;
  }
}
.header-right-spacer {
  width: 40px;
}

.admin-banner {
  height: 25px;
  width: 100%;
  background-color: var(--clementine-300);
  z-index: 1000;
  align-items: center;
  display: flex;
  top: 69px;
  position: fixed;
}

@media (min-width: 769px) {
  .platform-body-wrapper {
    display: flex;
    min-height: 100vh;
    flex-direction: column;
    background-color: transparent;
  }
  .platform-mobile-header-container {
    display: none;
    border-bottom: none;
  }
  .platform-container {
    display: flex;
    flex-direction: row;
    width: 100%;
  }
  .platform-sidebar {
    flex-direction: column;
    justify-content: flex-start;
    z-index: 1020;
    width: 183px;
    background-color: var(--salt);
    position: fixed;
    border-top-right-radius: var(--cr-br-large);
    border-bottom-right-radius: var(--cr-br-large);
    border: 1px solid var(--grape-jelly-100);
    border-left: none;
    left: 0;
    top: 0;
    height: 100vh;
    padding: var(--space-5, 24px) var(--space-4, 16px);
    display: inline-flex !important;

    .header-container {
      margin-bottom: var(--space-4);
      border-bottom: none;
      height: 54px;
      display: flex;
    }
  }
  .platform-body {
    width: 100%;
    padding-left: 183px;
  }
  .platform-content {
    --client-y-margin: var(--space-7);

    margin: var(--client-y-margin) var(--space-5);

    &--homepage {
      margin: 0;
    }

    &:has(.header-container--sticky) {
      margin-top: var(--space-5);
    }
  }
  .nav-button-container {
    display: none;
  }
  .logo-container {
    display: inline-block;
    padding-top: .3125rem;
    padding-bottom: .3125rem;
    margin-right: 1rem;
  }
  .nav-crafty-logo {
    width: 87px;
    height: 44px;
  }
  .admin-banner {
    top: 0px;
  }

  :global(.platform-body-wrapper .dashboard-page-title) {
    padding: 0;
    margin-bottom: var(--space-4);
  }
}

@media (max-width: 768px) {
  :global(.platform-body-wrapper .dashboard-page-title) {
    display: none !important;
  }
}
</style>
